// src/lib/conference/conference-signaling.ts
// WS-Client zur ConferenceRoom-DO. Puffert ausgehende Messages bis OPEN (publish/HELLO werden direkt
// nach connect() gefeuert, da ist der Socket noch CONNECTING — sonst InvalidStateError). Auto-Reconnect
// mit Identitäts-Erhalt (guestId/role), damit ein WS-Blip den Gast nicht aus dem Roster wirft.
import { parseConferenceMessage, serializeConferenceMessage, type ConferenceMessage } from '../../../shared/conference-protocol';

export type ConferenceSignalingHandlers = {
  onMessage: (msg: ConferenceMessage) => void;
  onOpen?: () => void;
  onReconnecting?: () => void;
  onClose?: () => void;
  /** Server-seitiger TERMINALER Close (Kick 4002 / Ended 4003 / Full 4001) bei noch offener Verbindung.
   *  Dient als Fallback, falls die zugehörige App-Message (KICKED/SESSION_ENDED) verloren ging. */
  onTerminated?: (code: number) => void;
};

export class ConferenceSignaling {
  private ws: WebSocket | null = null;
  private queue: string[] = [];
  private closed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /** `url` ist vollständig (inkl. wss:// + Token/PW bzw. ZT-Pfad); `hello` wird bei jedem open zuerst gesendet. */
  constructor(private url: string, private hello: ConferenceMessage, private handlers: ConferenceSignalingHandlers) {}

  open(): void { this.closed = false; this.connect(); }

  private connect(): void {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      ws.send(serializeConferenceMessage(this.hello));   // HELLO/HOST_HELLO zuerst
      for (const m of this.queue) ws.send(m);
      this.queue = [];
      this.handlers.onOpen?.();
    };
    ws.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      let msg: ConferenceMessage;
      try { msg = parseConferenceMessage(e.data); } catch { return; }
      this.handlers.onMessage(msg);
    };
    ws.onclose = (evt) => {
      this.ws = null;
      if (this.closed) { this.handlers.onClose?.(); return; }   // wir haben selbst geschlossen
      // Kick/Ended/Full sind terminal — nicht reconnecten (sonst „Geist" im Roster). onTerminated ist der
      // Fallback, falls die zugehörige App-Message (KICKED/SESSION_ENDED) verloren ging → Client beendet trotzdem.
      if (evt.code === 4002 || evt.code === 4003 || evt.code === 4001) { this.closed = true; this.handlers.onTerminated?.(evt.code); return; }
      this.handlers.onReconnecting?.();
      this.reconnectTimer = setTimeout(() => { if (!this.closed) this.connect(); }, 1000);
    };
    ws.onerror = () => { /* onclose folgt */ };
  }

  /** Sofort frische Leitung erzwingen (gezogenes WLAN lässt den alten Socket tot-aber-OPEN). */
  reconnectNow(): void {
    if (this.closed) return;
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    const old = this.ws; this.ws = null;
    if (old) { old.onopen = old.onmessage = old.onerror = old.onclose = null; try { old.close(); } catch { /* ignore */ } }
    this.connect();
  }

  send(msg: ConferenceMessage): void {
    const s = serializeConferenceMessage(msg);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(s);
    else this.queue.push(s);
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    this.queue = [];
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
  }
}
